'use client';

// ============================================================================
// CrewAhead Maritime Platform - useAuth Hook
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';
import {
  login as loginApi,
  register as registerApi,
  logout as logoutApi,
  getCurrentUser,
  updateAccountInfo as updateAccountInfoApi,
  setTokens,
} from '../lib/api';
import type { CrewAheadApiError } from '../lib/api/client';
import { QUERY_KEYS } from '../lib/types/api';
import type {
  AuthResponse,
  LoginCredentials,
  RegisterData,
  User,
} from '../lib/types';

// ─── Hook Return Type ────────────────────────────────────────────────────────

export interface UseAuthReturn {
  /** Current authenticated user, or null if not authenticated */
  user: User | null;
  /** Whether the auth state is being loaded */
  isLoading: boolean;
  /** Whether a login/register mutation is in progress */
  isAuthenticating: boolean;
  /** Whether the user is authenticated */
  isAuthenticated: boolean;
  /** Whether the user's email is verified */
  isEmailVerified: boolean;
  /** Profile completeness percentage (0-100) */
  profileCompleteness: number;
  /** Log in with credentials */
  login: (credentials: LoginCredentials) => Promise<void>;
  /** Register a new account */
  register: (data: RegisterData) => Promise<void>;
  /** Log out the current user */
  logout: () => Promise<void>;
  /** Authentication error, if any */
  error: CrewAheadApiError | null;
  /** Manually clear the auth error */
  clearError: () => void;
  /** Refetch the current user */
  refetchUser: () => Promise<void>;
  /** Update account info (name, phone, nationality, DOB) */
  updateAccountInfo: (data: {
    firstName?: string;
    lastName?: string;
    phone?: string;
    nationality?: string;
    nationalityCountryId?: number | null;
    dateOfBirth?: string;
    countryOfResidence?: string;
    countryOfResidenceCountryId?: number | null;
    city?: string;
    cityCountryId?: number | null;
  }) => Promise<User>;
}

// ─── useAuth Hook ────────────────────────────────────────────────────────────

/**
 * Hook for managing authentication state and operations.
 *
 * Wraps the auth API functions with the Zustand auth store and
n * TanStack Query for server state management.
 *
 * @example
 * ```tsx
 * const { user, isAuthenticated, login, logout, isLoading } = useAuth();
 *
 * if (isLoading) return <LoadingSpinner />;
 * if (!isAuthenticated) return <LoginForm />;
 *
 * return <Dashboard user={user} />;
 * ```
 */
export function useAuth(): UseAuthReturn {
  const queryClient = useQueryClient();
  const [error, setError] = useState<CrewAheadApiError | null>(null);

  // Zustand store selectors
  const storeUser = useAuthStore((state) => state.user);
  const storeSetUser = useAuthStore((state) => state.setUser);
  const storeSetToken = useAuthStore((state) => state.setToken);
  const storeLogout = useAuthStore((state) => state.logout);
  const storeHydrate = useAuthStore((state) => state.hydrate);
  const storeIsAuthenticated = useAuthStore((state) => state.isAuthenticated);

  // ─── Hydrate store on mount ────────────────────────────────────────────────

  useEffect(() => {
    storeHydrate();
  }, [storeHydrate]);

  // ─── Fetch current user query ──────────────────────────────────────────────

  const {
    data: userData,
    isLoading: isUserLoading,
    refetch: refetchUserQuery,
  } = useQuery({
    queryKey: QUERY_KEYS.AUTH.USER,
    queryFn: async (): Promise<User | null> => {
      const token = useAuthStore.getState().token;
      if (!token) return null;

      try {
        const user = await getCurrentUser();
        storeSetUser(user);
        return user;
      } catch {
        // If fetching user fails, clear auth state
        storeLogout();
        return null;
      }
    },
    enabled: storeIsAuthenticated,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false,
  });

  // ─── Login Mutation ────────────────────────────────────────────────────────

  const loginMutation = useMutation<AuthResponse, CrewAheadApiError, LoginCredentials>({
    mutationFn: loginApi,
    onSuccess: (data) => {
      // Safety: loginApi also calls setTokens(), but this ensures sync
      setTokens(data.accessToken, data.refreshToken);
      storeSetToken(data.accessToken);
      storeSetUser(data.user);
      setError(null);
      console.info('[Auth] Login success — tokens stored');

      // Invalidate and refetch user-related queries
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.AUTH.USER });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PROFILE.DETAIL(data.user.id),
      });
    },
    onError: (err) => {
      console.error('[Auth] Login failed:', err.message);
      setError(err);
    },
  });

  // ─── Register Mutation ─────────────────────────────────────────────────────

  const registerMutation = useMutation<AuthResponse, CrewAheadApiError, RegisterData>({
    mutationFn: registerApi,
    onSuccess: (data) => {
      // Safety: store tokens in localStorage (registerApi also does this,
      // but this ensures the Zustand store and localStorage stay in sync)
      setTokens(data.accessToken, data.refreshToken);
      storeSetToken(data.accessToken);
      storeSetUser(data.user);
      setError(null);
      console.info('[Auth] Registration success — tokens stored');

      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.AUTH.USER });
    },
    onError: (err) => {
      console.error('[Auth] Registration failed:', err.message);
      setError(err);
    },
  });

  // ─── Logout Mutation ───────────────────────────────────────────────────────

  const logoutMutation = useMutation<void, CrewAheadApiError, void>({
    mutationFn: logoutApi,
    onSuccess: () => {
      storeLogout();
      setError(null);

      // Clear all queries from cache on logout
      queryClient.clear();
    },
    onError: () => {
      // Even if the server request fails, clear local state
      storeLogout();
      queryClient.clear();
    },
  });

  // ─── Actions ───────────────────────────────────────────────────────────────

  const login = useCallback(
    async (credentials: LoginCredentials): Promise<void> => {
      setError(null);
      await loginMutation.mutateAsync(credentials);
    },
    [loginMutation]
  );

  const register = useCallback(
    async (data: RegisterData): Promise<void> => {
      setError(null);
      await registerMutation.mutateAsync(data);
    },
    [registerMutation]
  );

  const logout = useCallback(async (): Promise<void> => {
    setError(null);
    await logoutMutation.mutateAsync();
  }, [logoutMutation]);

  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  const refetchUser = useCallback(async (): Promise<void> => {
    await refetchUserQuery();
  }, [refetchUserQuery]);

  // ─── Update Account Info Mutation ──────────────────────────────────────────

  const updateAccountInfoMutation = useMutation<User, CrewAheadApiError, Parameters<typeof updateAccountInfoApi>[0]>({
    mutationFn: updateAccountInfoApi,
    onSuccess: (updatedUser) => {
      storeSetUser(updatedUser);
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.AUTH.USER });
      // Invalidate profile cache so profile view page reflects new name/nationality
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });

  const updateAccountInfo = useCallback(
    async (data: Parameters<typeof updateAccountInfoApi>[0]): Promise<User> => {
      return updateAccountInfoMutation.mutateAsync(data);
    },
    [updateAccountInfoMutation]
  );

  // ─── Derived State ─────────────────────────────────────────────────────────

  const user = userData ?? storeUser;
  const isLoading = isUserLoading || loginMutation.isPending || registerMutation.isPending;
  const isAuthenticating = loginMutation.isPending || registerMutation.isPending;
  const isAuthenticated = !!user && storeIsAuthenticated;
  const isEmailVerified = user?.isEmailVerified ?? false;
  const profileCompleteness = user?.profileCompleteness ?? 0;

  return {
    user,
    isLoading,
    isAuthenticating,
    isAuthenticated,
    isEmailVerified,
    profileCompleteness,
    login,
    register,
    logout,
    error,
    clearError,
    refetchUser,
    updateAccountInfo,
  };
}